import { faCheck, faHorizontalRule } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cn } from "@tosize/ui";
import { Checkbox } from "radix-ui";
import { type FC, type ReactNode, useMemo, useRef, useState } from "react";

interface FinishOptionCheckboxProps extends Checkbox.CheckboxProps {
	label: string | ReactNode;
	subtitle?: string;
	forceMount?: true;
}

export const FinishOptionCheckbox: FC<FinishOptionCheckboxProps> = (props) => {
	const { label, subtitle, children, forceMount, className, ...rest } = props;
	const isChecked = useMemo(() => !!rest.checked, [rest.checked]);
	const isIndeterminate = useMemo(() => rest.checked === "indeterminate", [rest.checked]);
	const ref = useRef<HTMLButtonElement>(null);
	const [focus, setFocused] = useState(false);

	return (
		<div
			className={cn("rounded border motion-safe:transition", {
				"border-shades bg-shades-light": rest.disabled,
				"border-primary hover:border-primary-medium":
					!focus && (isIndeterminate || isChecked) && !rest.disabled,
				"border-shades hover:border-primary": !(
					focus ||
					isIndeterminate ||
					isChecked ||
					rest.disabled
				),
				"ring-1": focus,
				"border-primary-medium ring-primary-medium":
					focus && (isIndeterminate || isChecked) && !rest.disabled,
				"border-primary ring-primary": focus && !isIndeterminate && !isChecked && !rest.disabled,
			})}
		>
			<Checkbox.Root
				{...rest}
				ref={ref}
				className="flex w-full cursor-pointer items-center justify-start gap-4 p-4 text-left outline-none disabled:cursor-not-allowed"
				onFocus={() => setFocused(true)}
				onBlur={() => setFocused(false)}
			>
				<div
					className={cn("flex h-5 w-5 border", {
						"border-primary-medium": !rest.disabled,
						"border-shades-medium": rest.disabled,
					})}
				>
					<Checkbox.Indicator
						className={cn({
							"flex h-full w-full items-center justify-center bg-primary-medium":
								isIndeterminate || isChecked,
						})}
						forceMount={forceMount}
					>
						{isIndeterminate ? (
							<FontAwesomeIcon
								icon={faHorizontalRule}
								size="sm"
								className="max-w-[75%] text-white"
							/>
						) : (
							isChecked && (
								<FontAwesomeIcon icon={faCheck} fixedWidth size="sm" className="text-white" />
							)
						)}
					</Checkbox.Indicator>
				</div>

				<div>
					{typeof label === "string" ? (
						<div className="inline-block font-bold">{label}</div>
					) : (
						label
					)}

					{!!subtitle && (
						<div className="ml-1 inline-block font-bold text-shades-dark text-xs">{subtitle}</div>
					)}
				</div>
			</Checkbox.Root>

			{isChecked && <div className={cn("px-4 pb-4", className)}>{children}</div>}
		</div>
	);
};
